import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftRight,
  Eye,
  Info,
  RotateCcw,
  X,
} from 'lucide-react';
import { verifyMindActivation } from '../api/dataService';
import { useLocale } from '../context/LocaleContext';
import {
  MindOptionCard as OptionCard,
  MindPreviewCard,
  ResponseSlots,
  SectionHeading,
  StageProgress,
  UtilityActions,
} from './mind/MindGamePrimitives';
import {
  arraysEqual,
  buildMindSessionSummary,
  calculateMindRoundScore,
  normalizeMindRounds,
  unorderedArraysEqual,
} from '../utils/mindSession';

const PREVIEW_INTERACTIONS = new Set(['sequence_recall', 'grid_recall', 'filtered_sequence']);
const NOOP = () => {};

function RoundMetaBadge({ children }) {
  if (!children) {
    return null;
  }

  return (
    <span className="rounded-full border border-cyan-200/20 bg-cyan-300/10 px-3 py-1 text-[11px] font-semibold text-cyan-100">
      {children}
    </span>
  );
}

function GridBoard({ round, selectedCellIds, previewVisible, activeFlashCellId, onToggleCell, disabled, t }) {
  const activePreviewCells = new Set(previewVisible
    ? activeFlashCellId
      ? [activeFlashCellId]
      : round?.previewCellIds || round?.targetCellIds || []
    : []);
  const selectedCells = new Set(selectedCellIds);
  const distractorCells = new Set(previewVisible ? round?.distractorCellIds || [] : []);
  const rows = Number(round?.grid?.rows || 0);
  const cols = Number(round?.grid?.cols || 0);

  return (
    <div
      className="grid gap-2 rounded-[1.75rem] border border-white/10 bg-slate-950/55 p-4"
      style={{ gridTemplateColumns: `repeat(${cols || 1}, minmax(0, 1fr))` }}
    >
      {(round?.grid?.cellIds || []).map((cellId, index) => {
        const isPreviewActive = activePreviewCells.has(cellId);
        const isDistractor = distractorCells.has(cellId);
        const isSelected = selectedCells.has(cellId);
        const selectedOrder = selectedCellIds.indexOf(cellId);
        return (
          <button
            key={cellId}
            type="button"
            onClick={() => onToggleCell(cellId)}
            disabled={disabled || previewVisible}
            aria-pressed={isSelected}
            aria-label={t('mindSession.gridCellLabel', 'Grid cell {number}{selected}', {
              number: index + 1,
              selected: isSelected ? t('mindSession.gridCellSelected', ', selected') : '',
            })}
            className={`aspect-square rounded-2xl border text-xs font-black transition ${
              isPreviewActive
                ? 'border-cyan-100/60 bg-cyan-300 text-slate-950'
                : isDistractor
                  ? 'border-amber-200/45 bg-amber-300/15 text-amber-100'
                : isSelected
                  ? 'border-cyan-100/50 bg-cyan-300/18 text-cyan-50'
                  : 'border-white/10 bg-white/[0.04] text-slate-500 hover:border-cyan-200/30 hover:text-white'
            }`}
          >
            {isSelected && round?.orderedRecall
              ? selectedOrder + 1
              : rows && cols
                ? `${Math.floor(index / cols) + 1}:${(index % cols) + 1}`
                : index + 1}
          </button>
        );
      })}
    </div>
  );
}

function resolveSessionBadge(t, summary) {
  if (!summary) {
    return '';
  }
  return t('mindSession.completeBadge', 'Mind Complete');
}

const MindSessionModal = ({ isOpen, quest, onClose, onComplete }) => {
  const { locale, t } = useLocale();
  const rounds = useMemo(() => (
    normalizeMindRounds(
      Array.isArray(quest?.detailedDescription?.session?.rounds)
        ? quest.detailedDescription.session.rounds
        : [],
      locale
    )
  ), [locale, quest]);
  const scoring = quest?.detailedDescription?.session || {};
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedCellIds, setSelectedCellIds] = useState([]);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStageResponses, setCompletedStageResponses] = useState([]);
  const [classificationByCard, setClassificationByCard] = useState({});
  const [failedChecksByRound, setFailedChecksByRound] = useState({});
  const [revealedIncorrectSelectionsByRound, setRevealedIncorrectSelectionsByRound] = useState({});
  const [roundResults, setRoundResults] = useState([]);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionPayload, setCompletionPayload] = useState(null);
  const [completionSummary, setCompletionSummary] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewReplayToken, setPreviewReplayToken] = useState(0);
  const [previewUsesByRound, setPreviewUsesByRound] = useState({});
  const [previewSecondsRemaining, setPreviewSecondsRemaining] = useState(10);
  const [activeFlashCellId, setActiveFlashCellId] = useState('');
  const [previewStartedRoundId, setPreviewStartedRoundId] = useState('');
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const dialogRef = useRef(null);
  const previousFocusedElementRef = useRef(null);

  const currentRound = rounds[currentRoundIndex] || null;
  const awaitingPreviewStart = currentRound?.interactionType === 'filtered_sequence'
    && previewStartedRoundId !== currentRound?.id;
  const currentFailedChecks = failedChecksByRound[currentRound?.id] || 0;
  const cardsById = useMemo(() => {
    const source = currentRound?.optionCards || currentRound?.previewCards || [];
    return Object.fromEntries(source.map((card) => [card.id, card]));
  }, [currentRound]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCurrentRoundIndex(0);
    setSelectedOrderIds([]);
    setSelectedCellIds([]);
    setSelectedTargetIds([]);
    setCurrentStageIndex(0);
    setCompletedStageResponses([]);
    setClassificationByCard({});
    setFailedChecksByRound({});
    setRevealedIncorrectSelectionsByRound({});
    setRoundResults([]);
    setFeedback({ type: '', message: '' });
    setIsSubmitting(false);
    setCompletionPayload(null);
    setCompletionSummary(null);
    setPreviewVisible(false);
    setPreviewReplayToken(0);
    setPreviewUsesByRound({});
    setPreviewSecondsRemaining(10);
    setActiveFlashCellId('');
    setPreviewStartedRoundId('');
    setShowDiscardConfirmation(false);
  }, [isOpen, quest?._id]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousFocusedElementRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    window.requestAnimationFrame(() => dialog?.querySelector(focusableSelector)?.focus());

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowDiscardConfirmation(true);
        return;
      }
      if (event.key !== 'Tab' || !dialog) {
        return;
      }
      const focusable = [...dialog.querySelectorAll(focusableSelector)];
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusedElementRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!currentRound) {
      return;
    }

    setSelectedOrderIds([]);
    setSelectedCellIds([]);
    setSelectedTargetIds([]);
    setCurrentStageIndex(0);
    setCompletedStageResponses([]);
    setClassificationByCard({});
    setFeedback({ type: '', message: '' });
    setRevealedIncorrectSelectionsByRound((prev) => {
      if (!currentRound?.id || !prev[currentRound.id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[currentRound.id];
      return next;
    });

    if (currentRound.interactionType === 'filtered_sequence' && previewStartedRoundId !== currentRound.id) {
      setPreviewVisible(false);
      return undefined;
    }

    if (PREVIEW_INTERACTIONS.has(currentRound.interactionType) && Number(currentRound.previewDurationMs) > 0) {
      setPreviewVisible(true);
      setPreviewUsesByRound((prev) => ({
        ...prev,
        [currentRound.id]: Math.min(
          Number(currentRound.previewsPerAttempt || 2),
          (prev[currentRound.id] || 0) + 1
        ),
      }));
      setPreviewSecondsRemaining(Math.ceil(Number(currentRound.previewDurationMs) / 1000));
      const countdownId = window.setInterval(() => {
        setPreviewSecondsRemaining((seconds) => Math.max(0, seconds - 1));
      }, 1000);
      let flashIntervalId;
      if (currentRound.orderedRecall && currentRound.flashOrderCellIds?.length) {
        let flashIndex = 0;
        setActiveFlashCellId(currentRound.flashOrderCellIds[0]);
        flashIntervalId = window.setInterval(() => {
          flashIndex += 1;
          setActiveFlashCellId(currentRound.flashOrderCellIds[flashIndex] || '');
        }, Number(currentRound.flashDurationMs) || 1000);
      } else {
        setActiveFlashCellId('');
      }
      const timeoutId = window.setTimeout(() => {
        setPreviewVisible(false);
        setActiveFlashCellId('');
      }, Number(currentRound.previewDurationMs));
      return () => {
        window.clearTimeout(timeoutId);
        window.clearInterval(countdownId);
        if (flashIntervalId) {
          window.clearInterval(flashIntervalId);
        }
      };
    }

    setPreviewVisible(false);
    return undefined;
  }, [currentRound, previewReplayToken, previewStartedRoundId]);

  if (!isOpen || !quest) {
    return null;
  }

  const appendFailedCheck = (message) => {
    if (!currentRound) {
      return;
    }
    setFailedChecksByRound((prev) => ({
      ...prev,
      [currentRound.id]: currentFailedChecks + 1,
    }));
    setFeedback({
      type: 'error',
      message,
    });
    setPreviewUsesByRound((prev) => ({
      ...prev,
      [currentRound.id]: 0,
    }));
  };

  const clearRevealedIncorrectSelections = () => {
    if (!currentRound?.id) {
      return;
    }
    setRevealedIncorrectSelectionsByRound((prev) => {
      if (!prev[currentRound.id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[currentRound.id];
      return next;
    });
  };

  const revealIncorrectSelections = (expectedIds = []) => {
    if (!currentRound?.id) {
      return;
    }
    const expectedSet = new Set(expectedIds);
    const incorrectIds = selectedTargetIds.filter((cardId) => !expectedSet.has(cardId));
    setRevealedIncorrectSelectionsByRound((prev) => ({
      ...prev,
      [currentRound.id]: incorrectIds,
    }));
  };

  const handleSequenceSelect = (cardId) => {
    const reusable = currentRound?.interactionType === 'sequence_recall';
    const expectedLength = currentRound?.correctOrderIds?.length || 0;
    if (!currentRound
      || previewVisible
      || awaitingPreviewStart
      || completionPayload
      || isSubmitting
      || selectedOrderIds.length >= expectedLength
      || (!reusable && selectedOrderIds.includes(cardId))) {
      return;
    }
    setSelectedOrderIds((prev) => [...prev, cardId]);
    setFeedback({ type: '', message: '' });
  };

  const handleGridToggle = (cellId) => {
    if (!currentRound || previewVisible || completionPayload || isSubmitting) {
      return;
    }
    const maxCount = currentRound?.targetCellIds?.length || 0;
    setSelectedCellIds((prev) => {
      if (prev.includes(cellId)) {
        return prev.filter((value) => value !== cellId);
      }
      if (prev.length >= maxCount) {
        return prev;
      }
      return [...prev, cellId];
    });
    setFeedback({ type: '', message: '' });
  };

  const handleTargetSelect = (cardId) => {
    if (!currentRound || completionPayload || isSubmitting) {
      return;
    }

    const isSelected = selectedTargetIds.includes(cardId);
    if (isSelected) {
      setSelectedTargetIds((prev) => prev.filter((value) => value !== cardId));
      clearRevealedIncorrectSelections();
      setFeedback({ type: '', message: '' });
      return;
    }

    setSelectedTargetIds((prev) => [...prev, cardId]);
    clearRevealedIncorrectSelections();
    setFeedback({ type: '', message: '' });
  };

  const handleRuleStageSelect = (cardId) => {
    if (!currentRound || completionPayload || isSubmitting) {
      return;
    }

    if (selectedTargetIds.includes(cardId)) {
      setSelectedTargetIds((prev) => prev.filter((value) => value !== cardId));
      clearRevealedIncorrectSelections();
      setFeedback({ type: '', message: '' });
      return;
    }

    setSelectedTargetIds((prev) => [...prev, cardId]);
    clearRevealedIncorrectSelections();
    setFeedback({ type: '', message: '' });
  };

  const handleClassification = (cardId, categoryId) => {
    if (!currentRound || completionPayload || isSubmitting) {
      return;
    }
    setClassificationByCard((prev) => ({
      ...prev,
      [cardId]: categoryId,
    }));
    setFeedback({ type: '', message: '' });
  };

  const handleRemoveLast = () => {
    if (isSubmitting || completionPayload) {
      return;
    }
    setSelectedOrderIds((prev) => prev.slice(0, -1));
    setFeedback({ type: '', message: '' });
  };

  const handleClear = () => {
    if (isSubmitting || completionPayload) {
      return;
    }
    setSelectedOrderIds([]);
    setSelectedCellIds([]);
    setSelectedTargetIds([]);
    setClassificationByCard({});
    clearRevealedIncorrectSelections();
    setFeedback({ type: '', message: '' });
  };

  const finalizeSession = async (nextRoundResults) => {
    const sessionSummary = buildMindSessionSummary({
      archetype: quest?.detailedDescription?.archetype,
      rounds,
      roundResults: nextRoundResults,
    });
    const sessionBadge = resolveSessionBadge(t, sessionSummary);
    const finalSummary = {
      ...sessionSummary,
      sessionBadge,
    };

    setCompletionSummary(finalSummary);
    setIsSubmitting(true);
    setFeedback({
      type: 'info',
      message: t('mindSession.lockingQuest', 'Locking today\'s Mind quest...'),
    });

    try {
      const response = await verifyMindActivation(quest._id, finalSummary);
      setCompletionPayload(response.data || null);
      setFeedback({
        type: 'success',
        message: response.data?.message || t('mindSession.completedToday', 'Mind activated for today.'),
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || t('mindSession.submitFailed', 'Unable to record this session right now.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pushRoundResult = async () => {
    const response = currentRound.interactionType === 'grid_recall'
      ? [...selectedCellIds]
      : currentRound.interactionType === 'binary_classify'
        ? (currentRound.optionCards || []).map((card) => `${card.id}:${classificationByCard[card.id]}`)
      : currentRound.interactionType === 'tap_targets'
        || currentRound.interactionType === 'progression_choice'
        || currentRound.interactionType === 'rule_switch'
        ? currentRound.interactionType === 'rule_switch'
          ? [...completedStageResponses, [...selectedTargetIds]]
          : [...selectedTargetIds]
        : [...selectedOrderIds];
    const roundScore = calculateMindRoundScore(currentFailedChecks, scoring);
    const nextRoundResults = [
      ...roundResults,
      {
        roundId: currentRound.id,
        score: roundScore,
        failedChecks: currentFailedChecks,
        difficultyKey: currentRound.difficultyKey,
        interactionType: currentRound.interactionType,
        response,
      },
    ];

    if (currentRoundIndex === rounds.length - 1) {
      await finalizeSession(nextRoundResults);
      return;
    }

    setRoundResults(nextRoundResults);
    setCurrentRoundIndex((prev) => prev + 1);
    setFeedback({
      type: 'success',
      message: t('mindSession.roundCleared', 'Round cleared. Move to the next pattern.'),
    });
  };

  const handleCheckRound = async () => {
    if (!currentRound || isSubmitting || completionPayload || previewVisible || awaitingPreviewStart) {
      return;
    }

    if (currentRound.interactionType === 'reorder' || currentRound.interactionType === 'sequence_recall' || currentRound.interactionType === 'filtered_sequence') {
      const expected = currentRound.correctOrderIds || [];
      if (selectedOrderIds.length !== expected.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.finishSequence', 'Finish the sequence before checking the round.'),
        });
        return;
      }
      if (!arraysEqual(selectedOrderIds, expected)) {
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }
      await pushRoundResult();
      return;
    }

    if (currentRound.interactionType === 'grid_recall') {
      const expected = currentRound.targetCellIds || [];
      if (selectedCellIds.length !== expected.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.finishPattern', 'Finish the pattern before checking the round.'),
        });
        return;
      }
      const gridMatches = currentRound.orderedRecall
        ? arraysEqual(selectedCellIds, expected)
        : unorderedArraysEqual(selectedCellIds, expected);
      if (!gridMatches) {
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }
      await pushRoundResult();
      return;
    }

    if (currentRound.interactionType === 'tap_targets') {
      const expected = currentRound.targetIds || [];
      if (selectedTargetIds.length < expected.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.selectAllTargets', 'Select every target before checking the round.'),
        });
        return;
      }
      if (!unorderedArraysEqual(selectedTargetIds, expected)) {
        revealIncorrectSelections(expected);
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }
      await pushRoundResult();
      return;
    }

    if (currentRound.interactionType === 'progression_choice') {
      const expected = currentRound.targetIds || [];
      if (selectedTargetIds.length !== expected.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.chooseMissingStep', 'Choose the missing step before checking the round.'),
        });
        return;
      }
      if (!unorderedArraysEqual(selectedTargetIds, expected)) {
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }
      await pushRoundResult();
      return;
    }

    if (currentRound.interactionType === 'binary_classify') {
      const cards = currentRound.optionCards || [];
      if (Object.keys(classificationByCard).length !== cards.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.classifyEveryCard', 'Assign every card before checking the round.'),
        });
        return;
      }
      const response = cards.map((card) => `${card.id}:${classificationByCard[card.id]}`);
      if (!unorderedArraysEqual(response, currentRound.correctClassificationIds || [])) {
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }
      await pushRoundResult();
      return;
    }

    if (currentRound.interactionType === 'rule_switch') {
      const stage = currentRound?.stages?.[currentStageIndex];
      const expected = stage?.targetIds || [];
      if (selectedTargetIds.length < expected.length) {
        setFeedback({
          type: 'error',
          message: t('mindSession.finishRule', 'Complete the live rule before continuing.'),
        });
        return;
      }
      if (!unorderedArraysEqual(selectedTargetIds, expected)) {
        revealIncorrectSelections(expected);
        appendFailedCheck(t('mindSession.tryAgain', 'Incorrect. Review your answer and try again.'));
        return;
      }

      if (currentStageIndex < (currentRound?.stages?.length || 0) - 1) {
        setCompletedStageResponses((prev) => [...prev, [...selectedTargetIds]]);
        setCurrentStageIndex((prev) => prev + 1);
        setSelectedTargetIds([]);
        clearRevealedIncorrectSelections();
        setFeedback({
          type: 'success',
          message: t('mindSession.nextRuleReady', 'Rule cleared. Switch modes for the next block.'),
        });
        return;
      }

      await pushRoundResult();
    }
  };

  const handleDone = () => {
    if (!completionPayload) {
      return;
    }
    onComplete?.(completionPayload);
    onClose?.();
  };

  const requestClose = () => {
    if (completionPayload || (currentRoundIndex === 0
      && selectedOrderIds.length === 0
      && selectedCellIds.length === 0
      && selectedTargetIds.length === 0
      && Object.keys(classificationByCard).length === 0
      && roundResults.length === 0)) {
      onClose?.();
      return;
    }
    setShowDiscardConfirmation(true);
  };

  const selectedOrderCards = selectedOrderIds.map((cardId) => cardsById[cardId]).filter(Boolean);
  const availableOrderCards = currentRound?.interactionType === 'sequence_recall'
    ? (currentRound?.optionCards || [])
    : (currentRound?.optionCards || []).filter((card) => !selectedOrderIds.includes(card.id));
  const currentStage = currentRound?.stages?.[currentStageIndex] || null;
  const revealedIncorrectSelections = new Set(revealedIncorrectSelectionsByRound[currentRound?.id] || []);
  const progressValue = rounds.length > 0
    ? ((Math.min(currentRoundIndex, rounds.length - 1) + (completionPayload ? 1 : 0)) / rounds.length) * 100
    : 0;
  const resolvedSummary = completionPayload?.sessionSummary || completionSummary;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 overflow-hidden bg-[#030B12] text-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mind-session-title"
    >
      <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-[radial-gradient(circle_at_top,rgba(26,164,204,0.12),transparent_34%),linear-gradient(180deg,#06111A_0%,#030B12_100%)]">
        {completionPayload ? (
          <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-between px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(2rem,env(safe-area-inset-top))] sm:px-8">
            <div>
              <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-cyan-300" style={{ width: '100%' }} />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/70">
                {t('mindSession.completeLabel', 'Mind complete')}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight">
                {completionPayload?.xpGained ? `+${completionPayload.xpGained} XP` : t('mindSession.completedToday', 'Mind activated for today.')}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                {feedback.message || t('mindSession.completedBody', 'Today\'s Mind session is locked. Come back tomorrow for a new track.')}
              </p>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{t('mindSession.score', 'Score')}</p>
                <p className="mt-1 text-xl font-black">{resolvedSummary?.score || 0}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{t('mindSession.rounds', 'Rounds')}</p>
                <p className="mt-1 text-xl font-black">{resolvedSummary?.roundsCompleted || rounds.length}/{resolvedSummary?.totalRounds || rounds.length}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{t('mindSession.badge', 'Badge')}</p>
                <p className="mt-1 text-sm font-black">{resolvedSummary?.sessionBadge || '-'}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDone}
              className="mt-8 inline-flex items-center justify-center rounded-2xl bg-cyan-300 px-5 py-4 text-sm font-black text-slate-950 transition hover:bg-cyan-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100"
            >
              {t('mindSession.backToDashboard', 'Back to Dashboard')}
            </button>
          </div>
        ) : (
          <>
            <header className="shrink-0 border-b border-white/10 bg-[#06111A]/95 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-xl sm:px-8">
              <div className="mx-auto w-full max-w-6xl">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200">
                    {quest?.detailedDescription?.categoryLabel || t('mindSession.category', 'Mind activation')}
                    </p>
                    <h2 id="mind-session-title" className="mt-1 truncate text-2xl font-black tracking-tight sm:text-3xl">{quest.name}</h2>
                    <p className="mt-1 line-clamp-2 max-w-3xl text-xs leading-5 text-slate-400 sm:text-sm">
                      {quest?.detailedDescription?.summary || quest.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={isSubmitting}
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 bg-slate-950/45 text-slate-200 transition hover:border-white/30 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:opacity-50"
                    aria-label={t('mindSession.close', 'Close')}
                  >
                    <X className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${progressValue}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                  <span>{t('mindSession.roundProgress', 'Round {current} of {total}', { current: currentRoundIndex + 1, total: rounds.length || 0 })}</span>
                  <span className="rounded-full border border-cyan-200/15 bg-cyan-300/[0.08] px-2.5 py-1 text-cyan-100">{currentRound?.difficultyLabel || ''}</span>
                </div>
              </div>
            </header>

            <main className="flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8 sm:py-8">
              <div className="mx-auto w-full max-w-6xl">
              {!currentRound ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-slate-300">
                  {t('mindSession.unavailable', 'This Mind session is unavailable right now.')}
                </div>
              ) : (
                <>
                  <section className={`rounded-3xl border p-5 shadow-[0_24px_80px_rgba(0,0,0,0.22)] sm:p-6 ${
                    currentRound.interactionType === 'filtered_sequence'
                      ? 'border-amber-300/25 bg-amber-300/[0.06]'
                      : currentRound.rotationReference
                        ? 'border-violet-300/25 bg-violet-300/[0.06]'
                        : 'border-cyan-200/15 bg-cyan-300/[0.05]'
                  }`}>
                    <div className="flex flex-wrap gap-2">
                      <RoundMetaBadge>{quest?.detailedDescription?.archetypeLabel}</RoundMetaBadge>
                      <RoundMetaBadge>{quest?.detailedDescription?.trackLabel}</RoundMetaBadge>
                      {currentRound?.secondaryRuleLabel ? <RoundMetaBadge>{currentRound.secondaryRuleLabel}</RoundMetaBadge> : null}
                    </div>
                    <p className="mt-4 text-[10px] font-black uppercase tracking-[0.2em] text-cyan-100/70">
                      {currentRound.title}
                    </p>
                    <h3 className="mt-1 text-xl font-black tracking-tight sm:text-2xl">{currentRound.subtitle}</h3>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">{currentRound.prompt}</p>
                    {currentRound.interactionType === 'filtered_sequence' && currentRound.secondaryRuleLabel ? (
                      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-300/35 bg-amber-300/[0.1] px-4 py-3 text-amber-100">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-300 text-xs font-black text-slate-950" aria-hidden="true">!</span>
                        <p className="text-sm font-black leading-6">
                          {t('mindSession.filterRule', 'Select only {rule}, preserving their original order.', {
                            rule: currentRound.secondaryRuleLabel,
                          })}
                        </p>
                      </div>
                    ) : null}
                    {currentRound.interactionType === 'sequence_recall' ? (
                      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-cyan-200/15 bg-slate-950/35 px-4 py-3 text-xs font-bold text-cyan-100">
                        <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>
                          {currentRound.recallDirection === 'reverse'
                            ? t('mindSession.reverseOrderHint', 'Start with the final item and work backward.')
                            : t('mindSession.forwardOrderHint', 'Rebuild the sequence in the same order.')}
                        </span>
                        <span className="ml-auto rounded-full border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">
                          {t('mindSession.optionsCanRepeat', 'Options can repeat')}
                        </span>
                      </div>
                    ) : null}
                    {Array.isArray(currentRound.clues) && currentRound.clues.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        {currentRound.clues.map((clue, index) => (
                          <div key={`${currentRound.id}-clue-${index}`} className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-100">
                            {clue}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {currentRound?.interactionType === 'rule_switch' && currentStage ? (
                      <div className="mt-4 space-y-3 rounded-2xl border border-cyan-200/20 bg-slate-950/35 px-4 py-4">
                        <StageProgress
                          stages={currentRound.stages}
                          activeIndex={currentStageIndex}
                          label={t('mindSession.modeProgress', 'Mode')}
                        />
                        <p className="text-sm font-black text-cyan-50">{currentStage.prompt}</p>
                      </div>
                    ) : null}
                    {currentRound?.rotationReference ? (
                      <div className="mt-4 rounded-2xl border border-cyan-200/20 bg-slate-950/60 px-4 py-4">
                        <div className="flex items-center gap-4">
                          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-cyan-200/25 text-3xl" aria-hidden="true">
                            {currentRound.rotationReference.glyph}
                          </span>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-200/70">
                              {t('mindSession.rotationStart', 'Start orientation')}
                            </p>
                            <p className="mt-1 text-sm font-semibold text-cyan-50">
                              {currentRound.rotationReference.label}
                            </p>
                          </div>
                          <span
                            className="ml-auto text-3xl text-cyan-200"
                            aria-label={currentRound.rotationReference.direction === 'counterclockwise'
                              ? t('mindSession.counterClockwise', 'Counter-clockwise')
                              : t('mindSession.clockwise', 'Clockwise')}
                          >
                            {currentRound.rotationReference.direction === 'counterclockwise' ? '↺' : '↻'}
                          </span>
                        </div>
                      </div>
                    ) : null}
                    {currentRound?.tutorial && previewStartedRoundId !== currentRound.id ? (
                      <div className="mt-5 rounded-2xl border border-amber-200/30 bg-slate-950/40 px-4 py-5 text-amber-50">
                        <div className="flex items-center gap-2">
                          <Info className="h-5 w-5 text-amber-200" aria-hidden="true" />
                          <p className="text-xs font-black uppercase tracking-[0.16em]">{currentRound.tutorial.title}</p>
                        </div>
                        <p className="mt-3 text-sm leading-6">{currentRound.tutorial.body}</p>
                        <p className="mt-3 rounded-xl border border-amber-200/15 bg-amber-300/[0.08] px-3 py-3 text-xs leading-5">{currentRound.tutorial.example}</p>
                        <button
                          type="button"
                          onClick={() => setPreviewStartedRoundId(currentRound.id)}
                          className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100"
                        >
                          <Eye className="h-5 w-5" aria-hidden="true" />
                          {t('mindSession.startPreview', 'Start preview')}
                        </button>
                      </div>
                    ) : null}
                    {previewVisible ? (
                      <div className="mt-4 flex items-center justify-between rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm font-semibold text-amber-50">
                        <span className="flex items-center gap-2">
                          <Eye className="h-5 w-5 text-amber-200" aria-hidden="true" />
                          {t('mindSession.previewing', 'Memorize the live pattern before the response phase starts.')}
                        </span>
                        <span className="ml-3 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-amber-200/60 text-xl font-black">
                          {previewSecondsRemaining}
                        </span>
                      </div>
                    ) : null}
                    {!previewVisible
                      && PREVIEW_INTERACTIONS.has(currentRound.interactionType)
                      && (currentRound.interactionType !== 'filtered_sequence' || previewStartedRoundId === currentRound.id)
                      && Number(currentRound.previewDurationMs) > 0 ? (
                        <button
                          type="button"
                          onClick={() => setPreviewReplayToken((value) => value + 1)}
                          disabled={(previewUsesByRound[currentRound.id] || 0) >= Number(currentRound.previewsPerAttempt || 2)}
                          className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-amber-200/25 px-4 py-2 text-xs font-bold text-amber-100 transition hover:bg-amber-300/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          <RotateCcw className="h-4 w-4" aria-hidden="true" />
                          {t('mindSession.replayPreviewCount', 'Replay preview ({used}/{total})', {
                            used: previewUsesByRound[currentRound.id] || 0,
                            total: Number(currentRound.previewsPerAttempt || 2),
                          })}
                        </button>
                      ) : null}
                  </section>

                  {currentRound.interactionType === 'progression_choice' ? (
                    <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                      <div className="mb-4 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                        <p className="font-black text-cyan-200">{t('mindSession.signalLadder', 'Signal ladder')}</p>
                        <span className="text-amber-200">
                          {t('mindSession.missingStepProgress', 'Missing step {current} of {total}', {
                            current: Math.min(selectedTargetIds.length + 1, (currentRound.missingSteps || []).length),
                            total: (currentRound.missingSteps || []).length,
                          })}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/10 bg-slate-950/55 p-5 sm:gap-3">
                        {(currentRound.sequenceLabels || []).map((label, index) => (
                          <React.Fragment key={`${currentRound.id}-sequence-${index}`}>
                            <span className={`flex h-12 min-w-12 items-center justify-center rounded-xl border px-3 text-lg font-black ${
                              label === '?' ? 'border-amber-200/40 bg-amber-300/15 text-amber-100' : 'border-cyan-200/15 bg-cyan-300/[0.08] text-cyan-50'
                            }`}>
                              {label === '?'
                                ? (() => {
                                  const gapIndex = (currentRound.missingSteps || []).findIndex((step) => step.index === index);
                                  const selectedId = selectedTargetIds[gapIndex];
                                  const selectedOption = (currentRound.missingSteps?.[gapIndex]?.optionCards || [])
                                    .find((card) => card.id === selectedId);
                                  return selectedOption?.label || '?';
                                })()
                                : label}
                            </span>
                            {index < currentRound.sequenceLabels.length - 1 ? <span className="text-cyan-200/50">→</span> : null}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {((currentRound.missingSteps || [])[selectedTargetIds.length]?.optionCards || currentRound.optionCards || []).map((card) => (
                          <OptionCard
                            key={`${currentRound.id}-${card.id}`}
                            card={card}
                            onClick={() => setSelectedTargetIds((previous) => (
                              previous.length < (currentRound.targetIds || []).length
                                ? [...previous, card.id]
                                : previous
                            ))}
                            selected={selectedTargetIds.includes(card.id)}
                            disabled={isSubmitting}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {previewVisible && (currentRound.interactionType === 'sequence_recall' || currentRound.interactionType === 'filtered_sequence') ? (
                    <section className="mt-6 rounded-3xl border border-amber-200/15 bg-[#08141E]/80 p-4 sm:p-6">
                      <SectionHeading
                        title={currentRound.interactionType === 'filtered_sequence'
                          ? t('mindSession.rememberedSequence', 'Remembered sequence')
                          : t('mindSession.previewSequence', 'Live preview')}
                        supporting={currentRound.interactionType === 'filtered_sequence'
                          ? t('mindSession.studyTraits', 'Study the order and the traits of every card.')
                          : t('mindSession.memorizeOrder', 'Memorize each item in the order shown.')}
                      />
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                        {(currentRound.previewCards || []).map((card, index) => (
                          <MindPreviewCard key={`${currentRound.id}-preview-${card.id}-${index}`} card={card} index={index} />
                        ))}
                      </div>
                      {currentRound.interactionType === 'filtered_sequence' ? (
                        <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-slate-950/35 p-3 text-[10px] font-black uppercase tracking-[0.12em] text-slate-300 sm:grid-cols-4">
                          <span><span className="mr-1 text-amber-300">●</span>{t('mindSession.warmTrait', 'Warm')}</span>
                          <span><span className="mr-1 text-sky-300">●</span>{t('mindSession.coolTrait', 'Cool')}</span>
                          <span><span className="mr-1 text-emerald-300">○</span>{t('mindSession.roundedTrait', 'Rounded')}</span>
                          <span><span className="mr-1 text-violet-300">◇</span>{t('mindSession.angularTrait', 'Angular')}</span>
                        </div>
                      ) : null}
                    </section>
                  ) : null}

                  {(currentRound.interactionType === 'reorder'
                    || currentRound.interactionType === 'sequence_recall'
                    || currentRound.interactionType === 'filtered_sequence') && !awaitingPreviewStart ? (
                    <>
                      <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                        <SectionHeading
                          title={currentRound.interactionType === 'filtered_sequence'
                            ? t('mindSession.yourFilteredSequence', 'Your filtered sequence')
                            : currentRound.recallDirection === 'reverse'
                              ? t('mindSession.yourReverseSequence', 'Your reverse sequence')
                              : t('mindSession.yourOrder', 'Your order')}
                          supporting={currentRound.interactionType === 'filtered_sequence'
                            ? t('mindSession.placeMatchingCards', 'Place matching cards here in their original order.')
                            : t('mindSession.fillSlotsInOrder', 'Fill every numbered slot in order.')}
                          action={(
                            <UtilityActions
                              onUndo={handleRemoveLast}
                              onClear={handleClear}
                              undoDisabled={selectedOrderIds.length === 0 || isSubmitting || previewVisible}
                              clearDisabled={selectedOrderIds.length === 0 || isSubmitting || previewVisible}
                              undoLabel={t('mindSession.undo', 'Undo')}
                              clearLabel={t('mindSession.clear', 'Clear')}
                            />
                          )}
                        />
                        <ResponseSlots
                          cards={selectedOrderCards}
                          count={(currentRound.correctOrderIds || []).length}
                          emptyLabel={t('mindSession.tapCardBelow', 'Tap a card below')}
                        />
                      </section>

                      <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                        <SectionHeading
                          title={currentRound.interactionType === 'filtered_sequence'
                            ? t('mindSession.candidateCards', 'Candidate cards')
                            : t('mindSession.availableTiles', 'Available cards')}
                          supporting={currentRound.interactionType === 'filtered_sequence'
                            ? t('mindSession.selectFilteredCards', 'Select cards that match the rule while preserving their order.')
                            : null}
                        />
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                          {availableOrderCards.map((card) => (
                            <OptionCard
                              key={`${currentRound.id}-${card.id}`}
                              card={card}
                              onClick={() => handleSequenceSelect(card.id)}
                              selected={false}
                              disabled={isSubmitting || previewVisible}
                            />
                          ))}
                        </div>
                      </section>
                    </>
                  ) : null}

                  {currentRound.interactionType === 'grid_recall' ? (
                    <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                      <SectionHeading
                        title={currentRound.transformationLabel
                          ? t('mindSession.mirrorPattern', 'Mirror the pattern')
                          : t('mindSession.gridPrompt', 'Rebuild the flashed grid')}
                        supporting={currentRound.transformationLabel
                          ? t('mindSession.copyOppositeSide', 'Use the source as a reference and reproduce it on the opposite side.')
                          : t('mindSession.repeatFlashOrder', 'Tap the cells in the original flash order.')}
                        action={(
                          <UtilityActions
                            onClear={handleClear}
                            clearDisabled={selectedCellIds.length === 0 || isSubmitting || previewVisible}
                            clearLabel={t('mindSession.clear', 'Clear')}
                          />
                        )}
                      />
                      {currentRound.transformationLabel ? (
                        <div className="mb-4 flex items-center justify-center gap-3 rounded-2xl border border-violet-300/20 bg-violet-300/[0.08] px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-violet-200">
                          <span className="h-px flex-1 bg-cyan-200/20" />
                          <ArrowLeftRight className="h-5 w-5 shrink-0" aria-hidden="true" />
                          <span>{currentRound.transformationLabel}</span>
                          <span className="h-px flex-1 bg-cyan-200/20" />
                        </div>
                      ) : null}
                      {previewVisible && currentRound.distractorInstruction ? (
                        <p className="mb-3 rounded-xl border border-amber-200/20 bg-amber-300/10 px-3 py-2 text-xs font-semibold text-amber-100">
                          {currentRound.distractorInstruction}
                        </p>
                      ) : null}
                      <div className={`mx-auto grid max-w-4xl gap-4 ${currentRound.transformationLabel && !previewVisible ? 'sm:grid-cols-2' : ''}`}>
                        {currentRound.transformationLabel && !previewVisible ? (
                          <div>
                            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-violet-200">
                              {t('mindSession.sourcePattern', 'Source pattern')}
                            </p>
                            <GridBoard
                              round={currentRound}
                              selectedCellIds={[]}
                              previewVisible={true}
                              activeFlashCellId=""
                              onToggleCell={NOOP}
                              disabled={true}
                              t={t}
                            />
                          </div>
                        ) : null}
                        <div>
                          {currentRound.transformationLabel && !previewVisible ? (
                            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">
                              {t('mindSession.yourMirror', 'Your mirrored pattern')}
                            </p>
                          ) : null}
                          <GridBoard
                            round={currentRound}
                            selectedCellIds={selectedCellIds}
                            previewVisible={previewVisible}
                            activeFlashCellId={activeFlashCellId}
                            onToggleCell={handleGridToggle}
                            disabled={isSubmitting}
                            t={t}
                          />
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {(currentRound.interactionType === 'tap_targets' || currentRound.interactionType === 'rule_switch') ? (
                    <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                      <SectionHeading
                        title={currentRound.interactionType === 'rule_switch'
                          ? t('mindSession.ruleStage', 'Live rule set')
                          : t('mindSession.targetSet', 'Target field')}
                        supporting={t('mindSession.followVisualTrait', 'Follow the card’s visual trait. The printed word may conflict.')}
                        action={(
                          <UtilityActions
                            onClear={handleClear}
                            clearDisabled={selectedTargetIds.length === 0 || isSubmitting}
                            clearLabel={t('mindSession.clear', 'Clear')}
                          />
                        )}
                      />
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {(currentRound.optionCards || []).map((card) => {
                          const targetIds = new Set(currentRound.interactionType === 'rule_switch'
                            ? (currentStage?.targetIds || [])
                            : (currentRound.targetIds || []));
                          const isSelected = selectedTargetIds.includes(card.id);
                          const status = revealedIncorrectSelections.has(card.id) && !targetIds.has(card.id)
                            ? 'incorrect'
                            : 'default';
                          return (
                            <OptionCard
                              key={`${currentRound.id}-${card.id}`}
                              card={card}
                              onClick={() => (currentRound.interactionType === 'rule_switch' ? handleRuleStageSelect(card.id) : handleTargetSelect(card.id))}
                              selected={isSelected}
                              status={status}
                              disabled={isSubmitting}
                              presentation={currentRound.interactionType === 'tap_targets'
                                ? 'focus-filter'
                                : (currentRound.optionCards || []).every((optionCard) => (
                                  (optionCard?.toneKey === 'warm' || optionCard?.toneKey === 'cool')
                                  && (optionCard?.sourceShapeId || optionCard?.symbolType)
                                  && (optionCard?.sizeKey === 'large' || optionCard?.sizeKey === 'small')
                                  && (optionCard?.fillKey === 'filled' || optionCard?.fillKey === 'outlined')
                                ))
                                  ? 'mode-shift'
                                  : 'default'}
                            />
                          );
                        })}
                      </div>
                    </section>
                  ) : null}

                  {currentRound.interactionType === 'binary_classify' ? (
                    <section className="mt-6 rounded-3xl border border-white/10 bg-[#08141E]/80 p-4 sm:p-6">
                      <SectionHeading
                        title={t('mindSession.classifyCards', 'Classify every card')}
                        supporting={t('mindSession.chooseCategoryEveryCard', 'Choose one category for every item.')}
                        action={(
                          <span className="rounded-full border border-cyan-200/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black text-cyan-200">
                          {Object.keys(classificationByCard).length}/{(currentRound.optionCards || []).length}
                          </span>
                        )}
                      />
                      <div className="grid gap-3 md:grid-cols-2">
                        {(currentRound.optionCards || []).map((card) => (
                        <div key={`${currentRound.id}-${card.id}`} className="rounded-2xl border border-white/10 bg-slate-950/35 p-4">
                          <p className="text-sm font-black text-white">{card.label}</p>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {(currentRound.classificationOptions || []).map((option) => {
                              const selected = classificationByCard[card.id] === option.id;
                              return (
                                <button
                                  key={`${card.id}-${option.id}`}
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() => handleClassification(card.id, option.id)}
                                  className={`min-h-11 rounded-xl border px-3 py-3 text-xs font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 ${
                                    selected
                                      ? 'border-cyan-100/50 bg-cyan-300/20 text-white'
                                      : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-cyan-200/30'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      </div>
                    </section>
                  ) : null}

                  {feedback.message && (
                    <div className={`mt-6 rounded-2xl px-4 py-3 text-sm font-semibold ${
                      feedback.type === 'error'
                        ? 'border border-red-400/30 bg-red-500/12 text-red-100'
                        : feedback.type === 'success'
                          ? 'border border-emerald-400/30 bg-emerald-500/12 text-emerald-100'
                          : 'border border-cyan-300/25 bg-cyan-300/10 text-cyan-50'
                    }`} role="status" aria-live="polite">
                      {feedback.message}
                    </div>
                  )}
                </>
              )}
              </div>
            </main>

            <footer className="shrink-0 border-t border-white/10 bg-[#06111A]/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl sm:px-8">
              <div className="mx-auto w-full max-w-6xl">
              <div className="mb-3 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                <span>{t('mindSession.failedChecks', 'Failed checks')}: {currentFailedChecks}</span>
                <span>{t('mindSession.roundValue', '{value} pts max', { value: scoring.roundScoreMax || 100 })}</span>
              </div>
              <button
                type="button"
                onClick={handleCheckRound}
                disabled={!currentRound
                  || isSubmitting
                  || previewVisible
                  || awaitingPreviewStart
                  || (currentRound?.interactionType === 'binary_classify'
                    && Object.keys(classificationByCard).length !== (currentRound.optionCards || []).length)}
                className="inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-cyan-300 px-5 py-3 text-sm font-black text-slate-950 shadow-[0_12px_30px_rgba(34,211,238,0.2)] transition hover:bg-cyan-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400 disabled:shadow-none"
              >
                {isSubmitting
                  ? t('mindSession.saving', 'Saving...')
                  : currentRound?.interactionType === 'rule_switch' && currentStageIndex < ((currentRound?.stages?.length || 1) - 1)
                    ? t('mindSession.nextRule', 'Next rule')
                    : currentRoundIndex === rounds.length - 1
                      ? t('mindSession.completeQuest', 'Complete Mind quest')
                      : t('mindSession.checkRound', 'Check round')}
              </button>
              </div>
            </footer>
          </>
        )}
        {showDiscardConfirmation ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/90 p-6">
            <div className="w-full rounded-3xl border border-white/15 bg-[#0D1C26] p-6 shadow-2xl" role="alertdialog" aria-modal="true" aria-labelledby="mind-discard-title">
              <h3 id="mind-discard-title" className="text-xl font-black">
                {t('mindSession.discardTitle', 'Discard this Mind session?')}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                {t('mindSession.discardBody', 'Your current progress will be lost.')}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button type="button" autoFocus onClick={() => setShowDiscardConfirmation(false)} className="rounded-2xl border border-white/15 px-4 py-3 text-sm font-bold">
                  {t('mindSession.keepPlaying', 'Keep playing')}
                </button>
                <button type="button" onClick={onClose} className="rounded-2xl bg-red-400 px-4 py-3 text-sm font-black text-slate-950">
                  {t('mindSession.discard', 'Discard')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MindSessionModal;
